import { useMemo } from "react";
import { Link } from "react-router";
import {
  ChevronRight,
  Clock,
  MapPin,
  CheckCircle2,
  AlertTriangle,
  ImageOff,
  Play,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DynamicIcon } from "@/components/categories/DynamicIcon";
import { usePendingEvents } from "@/hooks/useEvents";
import { useAllEvents, useUpdateEventStatus } from "@/hooks/useEvents";
import { useCategories } from "@/hooks/useCategories";
import { useStats } from "@/hooks/useStats";
import { useAiSources, useTriggerAiRun } from "@/hooks/useAiAgent";
import { useRunScraper } from "@/hooks/useScrapers";
import { useSystemLogs } from "@/hooks/useSystemLogs";
import type { Category, EventResponse } from "@/types";
import { cn } from "@/lib/utils";
import { formatDateTime, formatTime, dayChip } from "./format";

function withHash(hex: string): string {
  return hex.startsWith("#") ? hex : `#${hex}`;
}

function SectionCard({
  title,
  sub,
  action,
  children,
  className,
}: {
  title: string;
  sub?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    // min-w-0 lets the card shrink inside its grid/flex track (tracks default to
    // min-width:auto), so long unbreakable titles `truncate` instead of forcing
    // the whole card — and the layout — to overflow.
    <div className={cn("min-w-0 rounded-xl border border-black/5 bg-pulso-surface p-5", className)}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="font-brand text-[17px] font-bold tracking-tight">{title}</h2>
        {action ?? (sub && <span className="text-xs text-pulso-muted">{sub}</span>)}
      </div>
      {children}
    </div>
  );
}

function CategoryChip({ category }: { category?: Category }) {
  if (!category) return <span className="text-pulso-muted">—</span>;
  const color = withHash(category.colorHex);
  return (
    <span
      className="inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
      style={{ background: `${color}1A`, color }}
    >
      {category.name}
    </span>
  );
}

function CardSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-12 animate-pulse rounded-lg bg-black/[0.04]" />
      ))}
    </div>
  );
}

// ─── Moderation queue ────────────────────────────────────────────────
export function ModerationQueueCard() {
  const { data: events, isLoading } = usePendingEvents();
  const { data: categories = [] } = useCategories();
  const updateStatus = useUpdateEventStatus();
  const items = (events ?? []).slice(0, 3);

  const act = (id: string, status: "approved" | "rejected", okMsg: string) =>
    updateStatus.mutate(
      { id, status },
      {
        onSuccess: () => toast.success(okMsg),
        onError: (e) =>
          toast.error("Operacja nie powiodła się", {
            description: e instanceof Error ? e.message : undefined,
          }),
      },
    );

  return (
    <SectionCard
      title="Kolejka moderacji"
      action={
        <Button asChild variant="ghost" size="sm" className="gap-1 text-pulso-muted">
          <Link to="/pending">
            Zobacz wszystkie <ChevronRight className="h-3.5 w-3.5" />
          </Link>
        </Button>
      }
    >
      {isLoading ? (
        <CardSkeleton />
      ) : items.length === 0 ? (
        <div className="py-8 text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-pulso-success/10 text-pulso-success">
            <CheckCircle2 className="h-6 w-6" />
          </div>
          <p className="font-brand font-bold">Brak oczekujących</p>
          <p className="text-sm text-pulso-muted">Wszystkie wydarzenia są zatwierdzone.</p>
        </div>
      ) : (
        <div>
          {items.map((e) => {
            const category = categories.find((c) => c.id === e.categoryId);
            return (
              <div
                key={e.id}
                className="flex items-center gap-3 border-b border-black/5 py-3 last:border-0"
              >
                {e.coverImage ? (
                  <img
                    src={e.coverImage.full}
                    alt=""
                    className="h-11 w-11 shrink-0 rounded-lg object-cover"
                  />
                ) : (
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-black/5 text-pulso-muted">
                    <ImageOff className="h-[18px] w-[18px]" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">{e.title}</div>
                  <div className="mt-0.5 flex items-center gap-1.5 text-xs text-pulso-muted">
                    <span>{formatDateTime(e.date)}</span>
                    <span>·</span>
                    <span className="truncate">{e.locationName}</span>
                  </div>
                </div>
                <CategoryChip category={category} />
                <div className="flex shrink-0 gap-1">
                  <Button
                    size="icon"
                    className="h-8 w-8 bg-pulso-success text-white hover:bg-pulso-success/90"
                    title="Zatwierdź"
                    disabled={updateStatus.isPending}
                    onClick={() => act(e.id, "approved", "Wydarzenie zatwierdzone")}
                  >
                    <CheckCircle2 className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="destructive"
                    className="h-8 w-8"
                    title="Odrzuć"
                    disabled={updateStatus.isPending}
                    onClick={() => act(e.id, "rejected", "Wydarzenie odrzucone")}
                  >
                    <AlertTriangle className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

// ─── Upcoming events ─────────────────────────────────────────────────
export function UpcomingEventsCard() {
  const { data, isLoading } = useAllEvents({ status: "approved", hidePast: true, limit: 4 });
  const { data: categories = [] } = useCategories();
  const items: EventResponse[] = useMemo(() => {
    const list = data?.data ?? [];
    return [...list].sort((a, b) => +new Date(a.date) - +new Date(b.date)).slice(0, 4);
  }, [data]);

  return (
    <SectionCard title="Nadchodzące">
      {isLoading ? (
        <CardSkeleton rows={4} />
      ) : items.length === 0 ? (
        <p className="py-6 text-center text-sm text-pulso-muted">Brak nadchodzących wydarzeń.</p>
      ) : (
        <div>
          {items.map((e) => {
            const category = categories.find((c) => c.id === e.categoryId);
            const chip = dayChip(e.date);
            return (
              <div
                key={e.id}
                className="flex items-center gap-3 border-b border-black/5 py-2.5 last:border-0"
              >
                <div className="min-w-[48px] rounded-lg bg-pulso-primary/10 px-2 py-1.5 text-center text-pulso-primary">
                  <div className="text-[11px] font-semibold uppercase">{chip.month}</div>
                  <div className="font-brand text-xl font-bold leading-none">{chip.day}</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">{e.title}</div>
                  <div className="mt-0.5 flex items-center gap-1.5 text-xs text-pulso-muted">
                    <Clock className="h-3 w-3" /> {formatTime(e.date)}
                    <span>·</span>
                    <MapPin className="h-3 w-3" />{" "}
                    <span className="truncate">{e.locationName}</span>
                  </div>
                </div>
                <CategoryChip category={category} />
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

// ─── Category breakdown ──────────────────────────────────────────────
// Real approved-event counts per category from GET /admin/stats
// (categoryBreakdown). Bars are scaled to the largest category so the leader
// is full-width; the "N łącznie" sub is the sum of approved events.
export function CategoryBreakdownCard() {
  const {
    data: categories = [],
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useCategories();
  const { data: stats, isLoading: statsLoading, error: statsError } = useStats();
  const isLoading = categoriesLoading || statsLoading;
  const error = categoriesError || statsError;

  const { withCounts, total, max } = useMemo(() => {
    const counts = new Map((stats?.categoryBreakdown ?? []).map((b) => [b.categoryId, b.count]));
    const rows = categories
      .map((c) => ({ ...c, count: counts.get(c.id) ?? 0 }))
      .filter((c) => c.count > 0)
      .sort((a, b) => b.count - a.count);
    return {
      withCounts: rows,
      total: rows.reduce((s, c) => s + c.count, 0),
      max: rows[0]?.count ?? 1,
    };
  }, [categories, stats]);

  // Hide the total sublabel on error so we don't show a misleading "0 łącznie".
  return (
    <SectionCard title="Wydarzenia wg kategorii" sub={error ? undefined : `${total} łącznie`}>
      {isLoading ? (
        <CardSkeleton rows={5} />
      ) : error ? (
        <p className="py-6 text-center text-sm text-pulso-orange">
          Nie udało się załadować danych kategorii.
        </p>
      ) : withCounts.length === 0 ? (
        <p className="py-6 text-center text-sm text-pulso-muted">Brak zatwierdzonych wydarzeń.</p>
      ) : (
        <div>
          {withCounts.map((c) => {
            const color = withHash(c.colorHex);
            const pct = Math.round((c.count / max) * 100);
            return (
              <div key={c.id} className="grid grid-cols-[110px_1fr_40px] items-center gap-3 py-2">
                <div className="flex items-center gap-2">
                  <span
                    className="flex h-6 w-6 items-center justify-center rounded-md"
                    style={{ background: `${color}20`, color }}
                  >
                    <DynamicIcon name={c.iconName} className="h-3 w-3" />
                  </span>
                  <span className="truncate text-[13px] font-medium">{c.name}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-black/5">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${pct}%`, background: color }}
                  />
                </div>
                <div className="text-right text-[13px] tabular-nums text-pulso-muted">
                  {c.count}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

// ─── Automation health ───────────────────────────────────────────────
export function AutomationHealthCard() {
  const { data: sources, isLoading } = useAiSources();
  const triggerAiRun = useTriggerAiRun();
  const runOck = useRunScraper();

  const aiTotal = sources?.length ?? 0;
  const aiHealthy = sources?.filter((s) => s.consecutiveFailures === 0).length ?? 0;
  const aiFailing = sources?.some((s) => s.consecutiveFailures >= 3) ?? false;
  const aiLastRun = useMemo(() => {
    const times = (sources ?? []).map((s) => s.lastRunAt).filter(Boolean) as string[];
    return times.sort().at(-1) ?? null;
  }, [sources]);

  const rows = [
    {
      key: "ai",
      name: "Agent AI",
      to: "/ai-agent/sources",
      healthy: aiHealthy,
      total: aiTotal,
      lastRun: aiLastRun,
      bad: aiFailing,
    },
    {
      key: "ock",
      name: "Skrobak OCK",
      to: "/scrapers/ock",
      healthy: 1,
      total: 1,
      lastRun: null,
      bad: false,
    },
  ];

  return (
    <SectionCard title="Automatyzacje" sub="stan na teraz">
      {isLoading ? (
        <CardSkeleton rows={2} />
      ) : (
        <div className="space-y-1">
          {rows.map((s) => (
            <Link
              key={s.key}
              to={s.to}
              className="-mx-2 flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-black/[0.03]"
            >
              <div
                className={cn(
                  "flex h-10 w-10 items-center justify-center rounded-[10px]",
                  s.bad
                    ? "bg-pulso-orange/10 text-pulso-orange"
                    : "bg-pulso-success/10 text-pulso-success",
                )}
              >
                {s.bad ? (
                  <AlertTriangle className="h-[18px] w-[18px]" />
                ) : (
                  <CheckCircle2 className="h-[18px] w-[18px]" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold">{s.name}</div>
                <div className="truncate text-xs text-pulso-muted">
                  {s.healthy}/{s.total} sprawne
                  {s.lastRun ? ` · ostatnie uruchomienie ${formatDateTime(s.lastRun)}` : ""}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-pulso-muted" />
            </Link>
          ))}
        </div>
      )}
      <div className="mt-2 flex gap-2 border-t border-black/5 pt-3">
        <Button
          variant="secondary"
          size="sm"
          className="gap-1.5"
          disabled={runOck.isPending}
          onClick={() =>
            runOck.mutate(undefined, {
              onSuccess: () => toast.success("Uruchomiono skrobak OCK"),
              onError: (e) =>
                toast.error("Nie udało się uruchomić skrobaka", {
                  description: e instanceof Error ? e.message : undefined,
                }),
            })
          }
        >
          <Play className="h-3 w-3" /> Skrobak OCK
        </Button>
        <Button
          variant="secondary"
          size="sm"
          className="gap-1.5"
          disabled={triggerAiRun.isPending}
          onClick={() =>
            triggerAiRun.mutate(undefined, {
              onSuccess: () => toast.success("Uruchomiono Agent AI"),
              onError: (e) =>
                toast.error("Nie udało się uruchomić agenta", {
                  description: e instanceof Error ? e.message : undefined,
                }),
            })
          }
        >
          <Sparkles className="h-3 w-3" /> Agent AI
        </Button>
      </div>
    </SectionCard>
  );
}

// ─── System activity feed ────────────────────────────────────────────
export function ActivityFeedCard() {
  const { data, isLoading } = useSystemLogs({ limit: 4 });
  const items = (data?.data ?? []).slice(0, 4);
  // Success reads as green; blue is reserved for neutral/in-progress states.
  const tone = (status: string) =>
    status === "failure" ? "error" : status === "partial" ? "warn" : "success";

  return (
    <SectionCard
      title="Aktywność systemu"
      action={
        <Button asChild variant="ghost" size="sm" className="gap-1 text-pulso-muted">
          <Link to="/system-logs">
            Pełne logi <ChevronRight className="h-3.5 w-3.5" />
          </Link>
        </Button>
      }
    >
      {isLoading ? (
        <CardSkeleton rows={4} />
      ) : items.length === 0 ? (
        <p className="py-6 text-center text-sm text-pulso-muted">Brak zdarzeń.</p>
      ) : (
        <div>
          {items.map((l) => (
            <div key={l.id} className="flex gap-3 border-b border-black/[0.04] py-3 last:border-0">
              <div className="flex w-7 justify-center pt-1">
                <span className="pulse-dot" data-tone={tone(l.status)} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px]">{l.message}</div>
                <div className="mt-0.5 text-xs text-pulso-muted">
                  <span className="font-mono lowercase">
                    {l.domain} / {l.action}
                  </span>
                  <span> · {formatDateTime(l.ts)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}
